import * as iconv from "iconv-lite";
import { forwardToPrinter } from "@/lib/trpc-client";

// 类型定义（本地定义以避免对缺失模块的依赖）
export type PrescriptionData = {
  use_for?: number;
  vertex_distance?: number;
  right_sphere?: number;
  right_cylinder?: number;
  right_cylinder_axis?: number | string;
  right_corrected_vision?: string;
  right_papillary_distance?: number;
  right_ocular_center_height?: number | string;
  right_add?: number;
  left_sphere?: number;
  left_cylinder?: number;
  left_cylinder_axis?: number | string;
  left_corrected_vision?: string;
  left_papillary_distance?: number;
  left_ocular_center_height?: number | string;
  left_add?: number;
  notes?: string;
};

export type CustomerData = {
  customer_name?: string;
  phone?: string;
  gender?: number;
  date_of_birth?: string;
  age_years?: number;
  age_recorded_at?: string;
  // 顾客基本信息的备注（和验光单备注区分开）
  customer_notes?: string;
  prescription?: PrescriptionData;
};

// ESC/POS 命令常量
const ESC = "\x1B";
const GS = "\x1D";
const ESC_RESET = ESC + "@";
const ESC_FEED = "\n";
const ESC_INIT = ESC + "@";
const ESC_BOLD_ON = ESC + "E\x01";
const ESC_BOLD_OFF = ESC + "E\x00";
const ESC_CENTER = ESC + "a\x01";
const ESC_LEFT = ESC + "a\x00";
const ESC_LARGE = ESC + "!\x11";
const ESC_NORMAL = ESC + "!\x00";
const GS_CUT_FULL = GS + "V\x00";
const GS_CUT_PARTIAL = GS + "V\x01";

// 清理 axis 值中的零宽字符
function sanitizeAxis(value: number | string | undefined | null): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "string") {
    const HIDDEN = "\u200B";
    const cleaned = value.replace(new RegExp(HIDDEN, "g"), "");
    if (cleaned === "" || cleaned === "-") return undefined;
    const n = parseInt(cleaned, 10);
    return Number.isNaN(n) ? undefined : n;
  }
  return value;
}

// 从 URL 加载图片并转换为 ESC/POS 位图
async function loadImageAsBitmap(url: string): Promise<Uint8Array | null> {
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("image load failed"));
      img.src = url;
    });
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const maxWidth = 384; // 58mm 机常见宽度
    const scale = Math.min(1, maxWidth / img.width);
    canvas.width = Math.floor(img.width * scale);
    canvas.height = Math.floor(img.height * scale);
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return convertToBitmap(imageData, canvas.width, canvas.height);
  } catch {
    return null;
  }
}

// 将 ImageData 转换为 ESC/POS 位图命令（GS v 0）
function convertToBitmap(imageData: ImageData, width: number, height: number): Uint8Array {
  const result: number[] = [];
  result.push(0x1d, 0x76, 0x30, 0x00); // GS v 0, m=0
  const byteWidth = Math.ceil(width / 8);
  result.push(byteWidth & 0xff, (byteWidth >> 8) & 0xff);
  result.push(height & 0xff, (height >> 8) & 0xff);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < byteWidth; x++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const pixelX = x * 8 + bit;
        if (pixelX < width) {
          const offset = (y * width + pixelX) * 4;
          const r = imageData.data[offset] ?? 0;
          const g = imageData.data[offset + 1] ?? 0;
          const b = imageData.data[offset + 2] ?? 0;
          const brightness = r * 0.299 + g * 0.587 + b * 0.114;
          if (brightness < 128) {
            byte |= 1 << (7 - bit);
          }
        }
      }
      result.push(byte);
    }
  }
  return new Uint8Array(result);
}

function formatDiopter(value: number): string {
  if (value === 0) return "0";
  return value > 0 ? `+${value}` : String(value);
}

// 生成 ESC/POS（完整单据）
export async function generateESCPOS(
  customerData: CustomerData,
  pageTitle: string,
  pd?: number,
  logoURL?: string,
  appendText?: string
): Promise<Uint8Array> {
  const parts: Uint8Array[] = [];
  const lines: string[] = [];
  // 初始化
  lines.push(ESC_INIT);

  // Logo 或标题
  if (logoURL && logoURL.trim()) {
    const logoBitmap = await loadImageAsBitmap(logoURL);
    if (logoBitmap) {
      const initBytes = iconv.encode(lines.join(""), "GB18030");
      parts.push(new Uint8Array(initBytes));
      lines.length = 0;
      parts.push(new Uint8Array(iconv.encode(ESC_CENTER, "GB18030")));
      parts.push(logoBitmap);
      parts.push(new Uint8Array(iconv.encode(ESC_LEFT + ESC_FEED + ESC_FEED, "GB18030")));
    } else {
      lines.push(ESC_CENTER, ESC_LARGE, ESC_BOLD_ON, pageTitle || "顾客信息", ESC_NORMAL, ESC_BOLD_OFF, ESC_LEFT, ESC_FEED, ESC_FEED);
    }
  } else {
    lines.push(ESC_CENTER, ESC_LARGE, ESC_BOLD_ON, pageTitle || "顾客信息", ESC_NORMAL, ESC_BOLD_OFF, ESC_LEFT, ESC_FEED, ESC_FEED);
  }

  // 基本信息
  const rawCustomerNotes = customerData.customer_notes;
  const customerNotes: string =
    typeof rawCustomerNotes === "string" ? rawCustomerNotes.trim() : "";
  const hasBasicInfo =
    customerData.customer_name ||
    customerData.phone ||
    customerData.gender !== undefined ||
    customerData.date_of_birth ||
    customerData.age_years !== undefined ||
    customerNotes;

  if (hasBasicInfo) {
    if (customerData.customer_name) lines.push(`姓名：${customerData.customer_name}`, ESC_FEED);
    if (customerData.phone) lines.push(`电话：${customerData.phone}`, ESC_FEED);
    if (customerData.gender !== undefined) {
      const genderText = customerData.gender === 0 ? "男" : customerData.gender === 1 ? "女" : "其他";
      lines.push(`性别：${genderText}`, ESC_FEED);
    }
    if (customerData.date_of_birth) {
      lines.push(`出生日期：${customerData.date_of_birth}`, ESC_FEED);
    } else if (customerData.age_years !== undefined) {
      lines.push(`年龄：${customerData.age_years}岁`);
      if (customerData.age_recorded_at) {
        const d = new Date(customerData.age_recorded_at);
        lines.push(`（记录时间：${d.toLocaleDateString("zh-CN")}）`);
      }
      lines.push(ESC_FEED);
    }

    // 用户信息备注：紧跟在基本信息后面
    if (customerNotes) {
      const text = customerNotes;
      const noteLines = text.split("\n");
      if (noteLines.length > 0) {
        // 先输出标签“备注：”再换行，然后逐行输出内容
        lines.push("备注：", ESC_FEED);
        for (let i = 0; i < noteLines.length; i++) {
          const line = noteLines[i] ?? "";
          // 即使是空字符串也输出换行，以保留备注中的空行
          lines.push(line, ESC_FEED);
        }
      }
    }
  }

  // 验光单
  if (customerData.prescription) {
    const p = customerData.prescription;
    lines.push(ESC_FEED, "------------------------------------------------", ESC_FEED);
    if (p.use_for !== undefined && p.use_for !== null) {
      const useForText = p.use_for === 0 ? "远用" : p.use_for === 1 ? "近用" : "";
      if (useForText) lines.push(`用途：${useForText}`, ESC_FEED);
    }
    lines.push("\tSPH\tCYL\tAXIS", ESC_FEED);
    // 右眼
    lines.push("R\t");
    lines.push(p.right_sphere !== undefined ? formatDiopter(p.right_sphere) : "", "\t");
    lines.push(p.right_cylinder !== undefined ? formatDiopter(p.right_cylinder) : "", "\t");
    const rightAxis = sanitizeAxis(p.right_cylinder_axis);
    lines.push(rightAxis !== undefined ? String(rightAxis) : "", ESC_FEED);
    // 左眼
    lines.push("L\t");
    lines.push(p.left_sphere !== undefined ? formatDiopter(p.left_sphere) : "", "\t");
    lines.push(p.left_cylinder !== undefined ? formatDiopter(p.left_cylinder) : "", "\t");
    const leftAxis = sanitizeAxis(p.left_cylinder_axis);
    lines.push(leftAxis !== undefined ? String(leftAxis) : "", ESC_FEED, ESC_FEED);
    // PD
    const pdValue = p.right_papillary_distance ?? p.left_papillary_distance ?? pd;
    if (pdValue !== undefined && pdValue !== null) lines.push(`PD: ${pdValue}`, ESC_FEED);
    // PH
    const phRaw = (p.right_ocular_center_height ?? p.left_ocular_center_height) as number | string | undefined | null;
    const phText = phRaw === undefined || phRaw === null || phRaw === "-" ? "" : String(phRaw);
    if (phText !== "") lines.push(`PH: ${phText}`, ESC_FEED);
    // ADD
    const addValue = p.right_add ?? p.left_add;
    if (addValue !== undefined && addValue !== null) lines.push(`ADD: ${addValue}`, ESC_FEED);

    // 验光单备注：紧跟在度数信息后面
    const rawPrescriptionNotes = p.notes;
    const prescriptionNotes: string =
      typeof rawPrescriptionNotes === "string" ? rawPrescriptionNotes.trim() : "";
    if (prescriptionNotes) {
      const text = prescriptionNotes;
      const noteLines = text.split("\n");
      if (noteLines.length > 0) {
        // 先输出标签“备注：”再换行，然后逐行输出内容
        lines.push("备注：", ESC_FEED);
        for (let i = 0; i < noteLines.length; i++) {
          const line = noteLines[i] ?? "";
          // 即使是空字符串也输出换行，以保留备注中的空行
          lines.push(line, ESC_FEED);
        }
      }
    }
    lines.push("------------------------------------------------", ESC_FEED);
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(
      now.getHours()
    ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
    lines.push(`打印时间: ${dateStr}`, ESC_FEED);
  }

  // 追加文本
  if (appendText && appendText.trim()) {
    lines.push(ESC_FEED, ESC_CENTER);
    for (const line of appendText.split("\n")) {
      lines.push(line, ESC_FEED);
    }
    lines.push(ESC_LEFT);
  }

  // 结束与切纸
  lines.push(ESC_FEED, ESC_FEED, ESC_FEED, GS_CUT_FULL);
  const text = lines.join("");
  const gb = iconv.encode(text, "GB18030");
  parts.push(new Uint8Array(gb));

  // 合并
  const total = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

// 仅基本信息
export async function generateBasicInfoESCPOS(
  customerData: CustomerData,
  pageTitle: string,
  logoURL?: string
): Promise<Uint8Array> {
  return generateESCPOS({ ...customerData, prescription: undefined }, pageTitle, undefined, logoURL);
}

// 仅验光数据
export async function generatePrescriptionESCPOS(
  customerData: CustomerData,
  pageTitle: string,
  pd?: number,
  logoURL?: string
): Promise<Uint8Array> {
  // 只打印验光（度数）信息，不包含顾客基本信息
  return generateESCPOS(
    {
      prescription: customerData.prescription,
    },
    pageTitle,
    pd,
    logoURL
  );
}

// 通过 tRPC forward 发送二进制 ESC/POS（以 base64 封装）
export async function printViaForward(peerId: string, printerIP: string, escposBytes: Uint8Array) {
  try {
    await forwardToPrinter(peerId, printerIP, escposBytes);
    return { ok: true as const };
  } catch (e) {
    return { ok: false as const, error: e instanceof Error ? e.message : String(e) };
  }
}


